import React, { useState, useEffect } from 'react';
import { FormControl, Select, MenuItem, CircularProgress } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faArrowDown, faMinus } from '@fortawesome/free-solid-svg-icons';
import { useDashboard } from '../../hooks/useDashboard';
import { useTranslation } from 'react-i18next';

const FinancialRevenueComparison = () => {
    const { t } = useTranslation();
    const { getFinancialRevenueComparison } = useDashboard();

    const [currentPeriod, setCurrentPeriod] = useState('this_month');
    const [previousPeriod, setPreviousPeriod] = useState('last_month');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const strings = t('financialAnalytics.revenue', { returnObjects: true });
    const periodOptions = t('financialAnalytics.filters.periods', { returnObjects: true });

    const filterValues = [
        'today', 'yesterday', 'last_7_days', 'last_30_days', 'last_90_days',
        'this_month', 'last_month', 'this_year'
    ];

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const res = await getFinancialRevenueComparison({
                    current_period: currentPeriod,
                    previous_period: previousPeriod
                });
                if (res.data) setData(res.data);
            } catch (err) {
                console.error('Error fetching revenue comparison:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [currentPeriod, previousPeriod, getFinancialRevenueComparison]);

    const getChangeColor = (val) => {
        const n = parseFloat(val);
        if (n > 0) return '#059669';
        if (n < 0) return '#dc2626';
        return '#6b7280';
    };

    const getChangeBg = (val) => {
        const n = parseFloat(val);
        if (n > 0) return '#ecfdf5';
        if (n < 0) return '#fef2f2';
        return '#f9fafb';
    };

    const getChangeIcon = (val) => {
        const n = parseFloat(val);
        if (n > 0) return faArrowUp;
        if (n < 0) return faArrowDown;
        return faMinus;
    };

    const selectSx = {
        borderRadius: 2,
        fontSize: '0.8rem',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#75E6DA' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#189AB4' },
    };

    return (
        <div style={cardStyle}>
            <div style={headerStyle}>
                <div>
                    <span style={titleStyle}>{strings.title}</span>
                    <div style={subtitleStyle}>Weight & orders comparison across periods</div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <Select value={currentPeriod} onChange={(e) => setCurrentPeriod(e.target.value)} sx={selectSx}>
                            {filterValues.map(v => (
                                <MenuItem key={v} value={v} sx={{ fontSize: '0.8rem' }}>{periodOptions[v] || v}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <Select value={previousPeriod} onChange={(e) => setPreviousPeriod(e.target.value)} sx={selectSx}>
                            {filterValues.map(v => (
                                <MenuItem key={v} value={v} sx={{ fontSize: '0.8rem' }}>{periodOptions[v] || v}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
            </div>

            <div style={{ padding: '16px 20px 20px' }}>
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
                        <CircularProgress size={28} />
                    </div>
                ) : data ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {/* Period cards */}
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            <div style={{ flex: '1 1 200px', border: '1px solid #e0f2fe', borderRadius: 12, padding: '14px 16px', backgroundColor: '#f0f9ff' }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                                    {strings.currentPeriod} · {periodOptions[data.current_period] || data.current_period}
                                </div>
                                <div style={{ fontSize: 22, fontWeight: 800, color: '#111827' }}>{parseFloat(data.current_weight_tons || 0).toFixed(3)} T</div>
                                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{data.current_orders} orders</div>
                            </div>
                            <div style={{ flex: '1 1 200px', border: '1px solid #f3f4f6', borderRadius: 12, padding: '14px 16px', backgroundColor: '#fafafa' }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                                    {strings.previousPeriod} · {periodOptions[data.previous_period] || data.previous_period}
                                </div>
                                <div style={{ fontSize: 22, fontWeight: 800, color: '#111827' }}>{parseFloat(data.previous_weight_tons || 0).toFixed(3)} T</div>
                                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{data.previous_orders} orders</div>
                            </div>
                        </div>

                        {/* Change badges */}
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            <div style={{ flex: '1 1 180px', borderRadius: 12, padding: '12px 16px', backgroundColor: getChangeBg(data.weight_change_percentage), border: `1px solid ${getChangeColor(data.weight_change_percentage)}22` }}>
                                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginBottom: 6 }}>{strings.weightChange}</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>
                                        {Math.abs(parseFloat(data.weight_change_tons || 0)).toFixed(3)} T
                                    </span>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, color: getChangeColor(data.weight_change_percentage), backgroundColor: '#fff', borderRadius: 20, padding: '2px 8px', border: `1px solid ${getChangeColor(data.weight_change_percentage)}33` }}>
                                        <FontAwesomeIcon icon={getChangeIcon(data.weight_change_percentage)} style={{ fontSize: 10 }} />
                                        {Math.abs(parseFloat(data.weight_change_percentage || 0)).toFixed(2)}%
                                    </span>
                                </div>
                            </div>
                            <div style={{ flex: '1 1 180px', borderRadius: 12, padding: '12px 16px', backgroundColor: getChangeBg(data.orders_change_percentage), border: `1px solid ${getChangeColor(data.orders_change_percentage)}22` }}>
                                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginBottom: 6 }}>{strings.ordersChange}</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>
                                        {Math.abs(data.orders_change || 0)} orders
                                    </span>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, color: getChangeColor(data.orders_change_percentage), backgroundColor: '#fff', borderRadius: 20, padding: '2px 8px', border: `1px solid ${getChangeColor(data.orders_change_percentage)}33` }}>
                                        <FontAwesomeIcon icon={getChangeIcon(data.orders_change_percentage)} style={{ fontSize: 10 }} />
                                        {Math.abs(parseFloat(data.orders_change_percentage || 0)).toFixed(2)}%
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

const cardStyle = {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    border: '1px solid #f3f4f6',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    overflow: 'hidden',
};
const headerStyle = {
    padding: '18px 20px 12px',
    borderBottom: '1px solid #f9fafb',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: 12,
};
const titleStyle = { fontSize: 15, fontWeight: 700, color: '#111827', display: 'block' };
const subtitleStyle = { fontSize: 12, color: '#9ca3af', marginTop: 2 };

export default FinancialRevenueComparison;
